import { useBrokerFeatureLoader } from "@rentiohq/shared-frontend/dist/redux/broker/broker.hooks";
import { EBrokerFeature } from "@rentiohq/shared-frontend/dist/types/broker.types";
import { showAlert } from "@rentiohq/shared-frontend/dist/utils/alert/alert.utils";
import api from "@rentiohq/shared-frontend/dist/utils/api/api.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { Loading } from "@rentiohq/web-shared/dist/components";
import React from "react";
import { Navigate } from "react-router-dom";

export const ProfileCalendar = () => {
  const hasFetched = React.useRef(false);
  const { isFetchingBroker, hasFeature: hasCalendarSync } =
    useBrokerFeatureLoader(EBrokerFeature.CalendarSync);
  const [statusData, setStatusData] = React.useState<{
    isConnected: boolean;
    email: string | null;
  } | null>(null);
  const [isFetchingStatus, setIsFetchingStatus] = React.useState(true);

  React.useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const code = searchParams.get("code");

    if (code && !hasFetched.current) {
      hasFetched.current = true;
      const redirectUri = window.location.origin + window.location.pathname;
      api
        .post("/cronofy/oauth-callback", {
          data: { code, redirectUri },
          isV2: true,
        })
        .then(() => {
          // Immediately wipe the ?code parameters and force a hard page sync
          // so the user visually sees the connection!
          window.location.href = window.location.pathname;
        })
        .catch((err: any) => {
          console.error("OAuth callback failed", err); // eslint-disable-line no-console
        });
    }
  }, []);

  const refetchStatus = React.useCallback(async () => {
    try {
      setIsFetchingStatus(true);
      const res = await api.get<any>("/cronofy/status", {
        isV2: true,
        params: { t: Date.now() },
      });
      setStatusData(res.data?.data);
    } catch (e) {
      console.error(e); // eslint-disable-line no-console
    } finally {
      setIsFetchingStatus(false);
    }
  }, []);

  React.useEffect(() => {
    refetchStatus();
  }, [refetchStatus]);

  const handleConnectCalendar = async () => {
    try {
      const redirectUri = window.location.origin + window.location.pathname;
      const response = await api.get<any>(
        `/cronofy/auth-url?redirectUri=${encodeURIComponent(redirectUri)}`,
        { isV2: true },
      );
      if (response.data?.data?.url) {
        window.location.href = response.data.data.url;
      } else if (response.data?.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Failed to generate Cronofy URL", error); // eslint-disable-line no-console
    }
  };

  const handleDisconnect = async () => {
    try {
      await api.delete("/cronofy/disconnect", { isV2: true });
      await refetchStatus();
    } catch (error) {
      showAlert({
        type: "error",
        error,
      });
    }
  };

  if (isFetchingBroker || isFetchingStatus) {
    return <Loading />;
  }

  if (!hasCalendarSync) {
    return <Navigate to="../personal" replace />;
  }

  const isConnected = statusData?.isConnected;

  return (
    <div style={{ padding: "32px", maxWidth: "600px" }}>
      <h2
        style={{ marginBottom: "16px", fontSize: "24px", fontWeight: "bold" }}
      >
        {getLocalizedText("cronofy.profile.title")}
      </h2>
      <p style={{ marginBottom: "24px", lineHeight: "1.5", color: "#666" }}>
        {getLocalizedText("cronofy.profile.description")}
      </p>

      {isConnected ? (
        <div
          style={{
            background: "#E6F6F2",
            border: "1px solid #00A87A",
            padding: "16px",
            borderRadius: "6px",
          }}
        >
          <h3
            style={{
              color: "#00A87A",
              marginBottom: "8px",
              fontWeight: "bold",
            }}
          >
            {getLocalizedText("cronofy.profile.active_sync")}
          </h3>
          <p style={{ marginBottom: "16px", color: "#333" }}>
            {statusData.email
              ? getLocalizedText("cronofy.profile.synced_account", {
                  email: statusData.email,
                })
              : getLocalizedText("cronofy.profile.securely_connected")}
          </p>
          <button
            onClick={handleDisconnect}
            style={{
              padding: "10px 20px",
              cursor: "pointer",
              background: "transparent",
              color: "#DC2626",
              border: "1px solid #DC2626",
              borderRadius: "6px",
              fontSize: "14px",
              fontWeight: "bold",
            }}
          >
            {getLocalizedText("cronofy.profile.button.disconnect")}
          </button>
        </div>
      ) : (
        <button
          onClick={handleConnectCalendar}
          style={{
            padding: "12px 24px",
            cursor: "pointer",
            background: "#00A87A",
            color: "white",
            border: "none",
            borderRadius: "6px",
            fontSize: "16px",
            fontWeight: "bold",
          }}
        >
          {getLocalizedText("cronofy.profile.button.connect")}
        </button>
      )}
    </div>
  );
};
